// Single source of truth for which authored gameplay systems the generic
// /play/<slug> runtime actually executes today, versus which only save to
// the project document and do nothing yet. Read by PublishPopover's
// pre-publish checklist and by the docs pages that describe these systems,
// so the two surfaces move together instead of drifting the way
// CHANGELOG.md and the Map Builder room tool drifted.
export type GameplayReadinessStatus = 'live' | 'not-yet';

export interface GameplayReadinessEntry {
  id: string;
  system: string;
  status: GameplayReadinessStatus;
  detail: string;
}

export const GAMEPLAY_READINESS: readonly GameplayReadinessEntry[] = [
  {
    id: 'logic-rules',
    system: 'Logic rules',
    status: 'live',
    detail: 'Authored when / if / then rules now run in every published project, evaluated by the same shared engine Sunder itself uses.',
  },
  {
    id: 'triggers',
    system: 'Trigger volumes',
    status: 'live',
    detail: 'Placed triggers now dispatch: teleport actually moves the player in 3D (2D teleport movement is still a follow-up), and every trigger kind reaches your Logic rules as a trigger-enter / trigger-exit event.',
  },
  {
    id: 'quests',
    system: 'Quests',
    status: 'not-yet',
    detail: 'Authored quests do not yet reach the runtime or the HUD - there is no quest-log widget yet for them to reach.',
  },
  {
    id: 'dialogue',
    system: 'Dialogue',
    status: 'live',
    detail: 'The Dialogue widget now shows your first authored line. Triggering a specific line from gameplay, or branching, is not wired yet.',
  },
  {
    id: 'win-conditions',
    system: 'Win conditions',
    status: 'live',
    detail: 'Authored win conditions now decide match outcomes: the moment one resolves, players see a win/lose result screen and further movement/actions stop, in both 2D and 3D.',
  },
  {
    id: 'multiplayer',
    system: 'Multiplayer',
    status: 'live',
    detail: 'A lightweight relay gives every published project a presence list, synchronised player movement, and shared Logic state - a variable a rule sets, a quest it starts or completes, a win/lose result - reaching every other connected player automatically, no authoring required. Good for turn-based and simple co-op. It is not a match server: one connected client is deputised to run Logic rules on behalf of the room, but nothing sent through the relay is simulated or validated - treat every player as someone who could be running modified code. Sunder alone runs a dedicated server-authoritative host with real simulation and hit registration.',
  },
];

export function notYetLiveGameplayReadiness(): GameplayReadinessEntry[] {
  return GAMEPLAY_READINESS.filter((entry) => entry.status === 'not-yet');
}
